import type React from 'react'
import {
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { useFloating, offset, flip, shift, autoPlacement } from '@floating-ui/react-dom'
import type { TourContext, TourStep } from './tourTypes.js';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { waitForElement } from './waitForElement.js'
import { useDialogPortal } from '../../Provider/DialogPortalContext.js'
import { useSelector } from '../../Flux/index.js';
import { currentTour, selectOriginPath } from '../../Flux/Slice/Tour/TourSlice.js'
import { 
  selectCurrentStep, 
  selectIsTourActive, 
  setCurrentStep, 
  setIsTourActive,
  addCompletedStep
} from '../../Flux/Slice/Tour/TourSlice.js';
import { useGetAuthenticatedUser } from '../../Flux/Selector/index.js'
import { tourMap } from './tourMeta.js'

type Props = object

/**
 * @deprecated The guided tours are broken and unused. Kept, not maintained, until we decide whether to revive
 * them: their steps point at top-bar elements that have since moved or gone.
 */
export const GuidedTour: React.FC<Props> = () => {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [showMask, setShowMask] = useState<boolean>(true)
  const [stepReady, setStepReady] = useState(false);

  const targetEl = useRef<HTMLElement | null>(null);
  const dispatch = useDispatch();

  const currentStep = useSelector(selectCurrentStep);
  const isTourActive = useSelector(selectIsTourActive);

  const navigate = useNavigate();
  const location = useLocation();

  const tourId = useSelector(currentTour);
  const steps = tourId ? tourMap[tourId] || [] : [];
  const step: TourStep | undefined = steps[currentStep];

  const [context, setContext] = useState<TourContext | undefined>(undefined)

  const { dialogRef, setDialogRef } = useDialogPortal()

  const user = useGetAuthenticatedUser()

  const { refs, floatingStyles } = useFloating({
    placement: showMask ? 'right' : 'top',
    middleware: [offset(8), autoPlacement({ padding: 8 }), flip(), shift()],
  });

  const originPath = useSelector(selectOriginPath);

  useEffect(() => {
    if (user) {
      setContext({
        user,
        dispatch,
        dialogRef,
        setDialogRef
      })
    }
  }, [dispatch, user, dialogRef, setDialogRef])

  const prepareStep = useCallback(async () => {
    if (!context) return
    setStepReady(false);
    if (!step) return;

    // If navigation is requested and path is different, navigate first
    if (step.navigate && step.navigate !== location.pathname) {
      await new Promise<void>((resolve) => {
        console.info("do navigate")
        navigate(step.navigate!);
        // Wait a tick to ensure DOM updates
        requestAnimationFrame(() => resolve());
      });
    }

    // Run optional preShow hook
    if (step.preShow) {
      await step.preShow(context);
    }

    // Find target element
    try {
      if (step.selector) {
        const el = await waitForElement(step.selector, 3000);
        targetEl.current = el;
        setTargetRect(el.getBoundingClientRect());
        refs.setReference(el);
        setShowMask(true)
      } else {
        setShowMask(false)
        const centerX = window.innerWidth / 2;
        const centerY = window.innerHeight / 2;

        const fakeRect = {
          top: centerY,
          left: centerX,
          width: 0,
          height: 0,
          bottom: centerY,
          right: centerX,
          x: centerX,
          y: centerY,
          toJSON: () => ({}),
        } as DOMRect;

        targetEl.current = null;
        setTargetRect(fakeRect);
        refs.setReference({
          getBoundingClientRect: () => fakeRect,
        } as Element);
      }

      setStepReady(true);
    } catch (err) {
      console.warn(`Tour step target not found: ${step.selector}`, err);
      console.warn(`Skipping step — selector not found`, step.selector);
      if (currentStep < steps.length - 1) {
        dispatch(setCurrentStep(currentStep + 1));
      } else {
        dispatch(setCurrentStep(-1)); // End the tour
        dispatch(setIsTourActive(false));
      }
    }
  }, [step, context, refs, location.pathname, navigate, currentStep, dispatch]);


  useEffect(() => {
    if (step && isTourActive) {
      void prepareStep();
    }
  }, [step, prepareStep, isTourActive]);

  const goToNextStep = async () => {
    if (!context) return
    if (step?.postShow) {
      await step.postShow(context);
    }

    if (currentStep < steps.length - 1) {
      dispatch(setCurrentStep(currentStep + 1));
      // Mark the current step as completed
      dispatch(addCompletedStep(currentStep));
    } else {
      dispatch(setCurrentStep(-1)); // Exit tour
      dispatch(setIsTourActive(false));
      if (originPath && originPath !== location.pathname) {
        console.info("do navigate 2")
        navigate(originPath);
      }
    }
  };

  useEffect(() => {
    if (!targetEl.current) return;

    const updateRect = () => {
      setTargetRect(targetEl.current!.getBoundingClientRect());
    };

    updateRect(); // Initial

    const observer = new ResizeObserver(updateRect);
    observer.observe(targetEl.current);

    // Also listen for window resize (which may affect layout)
    window.addEventListener('resize', updateRect);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateRect);
    };
  }, [targetEl.current]);

  useEffect(() => {
    if (!isTourActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key;
      if (key === 'Enter' || key === ' ' || key === 'ArrowRight') {
        e.preventDefault();
        goToNextStep();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isTourActive, goToNextStep]);

  const handleStopTour = async () => {
    if (context && step?.postShow) {
      await step.postShow(context);
    }
    if (context && step?.stop) {
      await step.stop(context);
    }

    dispatch(setCurrentStep(-1));
    dispatch(setIsTourActive(false));

    if (originPath && originPath !== location.pathname) {
      console.info("do navigate 2");
      navigate(originPath);
    }
  };

  // Don't render the tour if it's not active or if the step is not ready
  if (!isTourActive || !stepReady || !targetRect || !steps.length || currentStep >= steps.length || currentStep < 0) {
    return null;
  }

  return createPortal(
    <>
      {/* Dim background with cutout */}
      <svg
        className="fixed inset-0 z-1010 pointer-events-auto"
        width="100%"
        height="100%"
        onClick={(e) => e.stopPropagation()}
        onMouseMove={(e) => e.stopPropagation()}
      >
        <defs>
          <mask id="tour-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              <rect
                x={targetRect.left - 12}
                y={targetRect.top - 12}
                width={targetRect.width + 24}
                height={targetRect.height + 24}
                rx="12"
                ry="12"
                fill="black"
              />
          </mask>
        </defs>
        {/* Overlay with the mask applied */}
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.6)"
          {...(showMask ? { mask: 'url(#tour-mask)' } : {})}
        />
      </svg>

      {/* Optional visual ring */}
      { showMask && <div
        className="fixed z-1011 border-4 border-yellow-400 rounded-xl pointer-events-none transition-all duration-300"
        style={{
          top: targetRect.top - 8,
          left: targetRect.left - 8,
          width: targetRect.width + 16,
          height: targetRect.height + 16,
        }}
      />
      }

      {/* Floating Popover */}
      { showMask && <div
          ref={refs.setFloating}
          style={floatingStyles}
          className="z-1011 max-w-sm rounded-xl bg-base-100 p-4 shadow-xl border relative"
        >
          { boxContent(step, steps, currentStep, handleStopTour, goToNextStep) }
        </div>
      }
      {!showMask && <div className="fixed inset-0 flex items-center justify-center z-1011">
        <div className="bg-white p-6 rounded-xl shadow-xl max-w-[90vw] max-h-[90vh] overflow-auto">
          { boxContent(step, steps, currentStep, handleStopTour, goToNextStep) }
        </div>
      </div>
      }
    </>,
    document.body
  );
};

const boxContent = (
  step: TourStep,
  steps: TourStep[],
  currentStep: number,
  handleStopTour: () => void,
  goToNextStep: () => void
) => {
  return <>
    <div className="mt-1 text-xl">{step.content}</div>
    <div className="mt-4 flex gap-2 justify-end items-center">
      <div className={"grow text-sm text-gray-600"}>{currentStep + 1} / {steps.length}</div>
      <button className="btn btn-sm" onClick={handleStopTour}>
        Stop Tour
      </button>
      <button className="btn btn-primary btn-sm" onClick={goToNextStep}>
        {currentStep < steps.length - 1 ? 'Next' : 'Finish'}
      </button>
    </div>
    </>
}